/**
 * Always-on banner while a super admin is viewing as another user (TRUS-747).
 *
 * An amber frame around the whole window plus a pill at the top: whose account
 * this is, which org, the ticket, read-only or read-write, the time left, and
 * Exit. Refusals from the API ("read-only", "never allowed") appear under the
 * pill so the admin knows why a button did nothing. Renders nothing otherwise.
 *
 * Self-contained so it can sit in the staff shell and the member portal alike
 * (the portal has no staff providers).
 */
import { useEffect, useState } from 'react';
import { Eye, LogOut, PenLine, ShieldAlert } from 'lucide-react';
import {
  exitImpersonation,
  getImpersonation,
  handleImpersonationResponse,
  IMPERSONATION_BLOCKED_EVENT,
  type ImpersonationState,
} from '@/lib/impersonation';
import { getWeaveinApiUrl } from '@/src/config';
import { isFramedDocument } from '@/lib/desktopWindow';

const RULE_TEXT: Record<string, string> = {
  read_only: 'This session is read-only.',
  other_org: 'This session is limited to one organization.',
  credentials: 'Passwords, 2FA and sign-in settings cannot be changed for a user.',
  outbound_message: 'Messages, email and SMS are never sent as the user.',
  invitation: 'Invitations are never sent as the user.',
  signature: 'Nothing is signed as the user.',
  claims_billing: 'Claims and billing are never submitted as the user.',
  payments: 'Payments and payouts are never made as the user.',
  calls_voice: 'Calls are never placed as the user.',
  external_system: 'Nothing is sent to outside systems as the user.',
  user_admin: 'User administration is not available here.',
  impersonation: 'Not available while viewing as another user.',
};

function formatRemaining(ms: number): string {
  if (ms <= 0) return '0:00';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * One banner per window: a page inside a tab-strip iframe draws none, because
 * the top window's banner already frames everything, tabs included.
 */
export function ImpersonationBanner() {
  if (isFramedDocument()) return null;
  return <ImpersonationBannerFrame />;
}

function ImpersonationBannerFrame() {
  const [state, setState] = useState<ImpersonationState | null>(() => getImpersonation());
  const [now, setNow] = useState(() => Date.now());
  const [notice, setNotice] = useState<string | null>(null);
  const [exiting, setExiting] = useState(false);

  // Another tab may start or end a session (shared localStorage).
  useEffect(() => {
    const onStorage = () => setState(getImpersonation());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (!state) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [state]);

  // Expiry returns to the admin even if nothing else is making requests.
  useEffect(() => {
    if (state && now >= state.expiresAt) void exitImpersonation('expired');
  }, [state, now]);

  // The server is the authority: re-check every minute so an ended or revoked
  // session (org opted out, another super admin ended it) is noticed promptly.
  useEffect(() => {
    if (!state) return;
    const check = async () => {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('member_token');
      if (!token) return;
      try {
        const res = await fetch(getWeaveinApiUrl('/api/impersonation/sessions/current'), {
          headers: { Authorization: `Bearer ${token}`, 'X-CBO-ID': String(state.organization.id) },
        });
        if (res.status === 401) {
          const body = await res.json().catch(() => ({ code: 'impersonation_ended' }));
          if (!handleImpersonationResponse(401, { code: 'impersonation_ended', ...body })) {
            void exitImpersonation('ended');
          }
        }
      } catch {
        /* network blip: the next check or the expiry timer handles it */
      }
    };
    const id = setInterval(check, 60_000);
    return () => clearInterval(id);
  }, [state]);

  useEffect(() => {
    const onBlocked = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      setNotice(RULE_TEXT[detail.rule] || detail.message || 'Not allowed while viewing as another user.');
    };
    window.addEventListener(IMPERSONATION_BLOCKED_EVENT, onBlocked);
    return () => window.removeEventListener(IMPERSONATION_BLOCKED_EVENT, onBlocked);
  }, []);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  if (!state) return null;

  const readWrite = state.mode === 'read_write';
  const who = state.target.name || state.target.email || `User ${state.target.id}`;
  const remaining = state.expiresAt - now;

  return (
    <>
      <div
        aria-hidden="true"
        className={`pointer-events-none fixed inset-0 z-[9998] border-4 ${readWrite ? 'border-red-500' : 'border-amber-500'}`}
      />
      <div
        role="status"
        aria-live="polite"
        className="fixed left-1/2 top-0 z-[9999] flex max-w-[calc(100vw-16px)] -translate-x-1/2 flex-col items-center"
      >
        <div
          className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-b-lg px-4 py-1.5 text-xs font-medium text-white shadow-lg ${
            readWrite ? 'bg-red-600' : 'bg-amber-600'
          }`}
        >
          {readWrite ? <PenLine className="h-3.5 w-3.5 shrink-0" /> : <Eye className="h-3.5 w-3.5 shrink-0" />}
          <span className="truncate">
            Viewing as <strong>{who}</strong>
            {state.target.kind === 'member' ? ' (member portal)' : state.target.role ? ` (${state.target.role})` : ''}
            {' · '}
            {state.organization.name}
          </span>
          <span className="rounded bg-white/20 px-1.5 py-0.5">{readWrite ? 'Read and write' : 'Read only'}</span>
          <span className="rounded bg-white/20 px-1.5 py-0.5">{state.ticketKey}</span>
          <span className="tabular-nums" title="Time left in this session">
            {formatRemaining(remaining)}
          </span>
          <button
            type="button"
            disabled={exiting}
            onClick={() => {
              setExiting(true);
              void exitImpersonation('exited');
            }}
            className="ml-1 inline-flex items-center gap-1 rounded bg-white px-2 py-0.5 text-xs font-semibold text-gray-900 hover:bg-gray-100 disabled:opacity-60"
          >
            <LogOut className="h-3.5 w-3.5" />
            {exiting ? 'Exiting…' : 'Exit'}
          </button>
        </div>
        {notice && (
          <div className="mt-1 flex items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg">
            <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-amber-400" />
            {notice}
          </div>
        )}
      </div>
    </>
  );
}

export default ImpersonationBanner;
